import React, { useState } from 'react'
import { Modal, Button, Table, Accordion, Card } from 'react-bootstrap'
import moment from 'moment'
import { OrganizationSettings } from 'interfaces/organization-settings'
import {
  useGHLUserMappings,
  type GHLUser,
  type OrganizationUser,
} from 'hooks/use-ghl-user-mappings'
import useDisableIntegrationConnection from 'use-disable-integration-connection'

interface IntegrationConnectionSettings {
  id?: number
  create_new_contacts?: boolean
  two_way_sync?: boolean
  log_against_deals?: boolean
  sync_call_recordings?: boolean
  sync_outgoing_answered_calls?: boolean
  sync_outgoing_unanswered_calls?: boolean
  sync_incoming_answered_calls?: boolean
  sync_incoming_missed_calls?: boolean
  sync_outgoing_voicemails?: boolean
  sync_incoming_sms?: boolean
  sync_outgoing_sms?: boolean
  sync_contacts?: boolean
  sync_calls?: boolean
  sync_messages?: boolean
  sync_email_messages?: boolean
  sync_email_open_events?: boolean
  sync_contact_notes?: boolean
  last_sync_at?: string
  zapier_api_key?: string
  webhook_url?: string
}

interface ConfiguredIntegration {
  id: number
  name: string
  type: string
  status: string
  enabled: boolean
  connected_at: string
  // GHL-specific fields
  ghl_company_id?: string
  ghl_location_id?: string
  state?: string
  version?: number
  sync_as_lead?: boolean
  // General integration fields
  webhook_url?: string
  instance_url?: string
  portal_id?: string
  tenant?: string
  // Integration connection settings
  settings?: IntegrationConnectionSettings
}

interface IntegrationConnectionDetailModalProps {
  show: boolean
  handleClose: () => void
  integration: ConfiguredIntegration | null
  organizationSettings?: OrganizationSettings
  orgId: string
}

const IntegrationConnectionDetailModal = ({
  show,
  handleClose,
  integration,
  organizationSettings,
  orgId,
}: IntegrationConnectionDetailModalProps) => {
  const [ghlMappingsOpen, setGhlMappingsOpen] = useState(true) // starts open
  const [syncSettingsOpen, setSyncSettingsOpen] = useState(false) // starts closed

  const disableMutation = useDisableIntegrationConnection()

  // Compute isGHL before hooks to maintain consistent hook call order
  const isGHL =
    integration?.type?.toLowerCase().includes('ghl') ||
    integration?.type?.toLowerCase().includes('gohighlevel') ||
    integration?.name?.toLowerCase().includes('gohighlevel')

  // All hooks must be called before any early returns
  const { data: ghlUserMappings, isLoading: isLoadingGHLMappings } =
    useGHLUserMappings(
      orgId,
      isGHL && integration ? integration.id : null,
      isGHL && show
    )

  if (!integration) return null

  const isHubSpot =
    integration.type?.toLowerCase().includes('hubspot') ||
    integration.name?.toLowerCase().includes('hubspot')

  const isAlreadyDisabled =
    integration.status === 'DISABLED' || integration.status === 'disabled'

  const handleDisable = () => {
    if (
      window.confirm(
        `Are you sure you want to DISABLE the integration "${integration.name}"? This action is permanent and cannot be undone. The connection cannot be re-enabled once disabled.`
      )
    ) {
      disableMutation.mutate(
        {
          integrationConnectionId: integration.id,
          organizationId: orgId,
        },
        {
          onSuccess: () => {
            handleClose()
          },
        }
      )
    }
  }

  const renderGHLSpecificDetails = () => {
    if (!isGHL) return null

    return (
      <div>
        <h6>GoHighLevel Details</h6>
        <Table striped bordered hover responsive>
          <tbody>
            {integration.ghl_company_id && (
              <tr>
                <td style={{ fontWeight: 'bold', width: '30%' }}>Company ID</td>
                <td>{integration.ghl_company_id}</td>
              </tr>
            )}
            {integration.ghl_location_id && (
              <tr>
                <td style={{ fontWeight: 'bold' }}>Location ID</td>
                <td>{integration.ghl_location_id}</td>
              </tr>
            )}
            {integration.state && (
              <tr>
                <td style={{ fontWeight: 'bold' }}>State</td>
                <td>
                  <code>{integration.state}</code>
                </td>
              </tr>
            )}
            {integration.version && (
              <tr>
                <td style={{ fontWeight: 'bold' }}>Version</td>
                <td>{integration.version}</td>
              </tr>
            )}
            {integration.sync_as_lead !== undefined && (
              <tr>
                <td style={{ fontWeight: 'bold' }}>Sync as Lead</td>
                <td>
                  <span
                    className={`badge ${
                      integration.sync_as_lead ? 'bg-success' : 'bg-secondary'
                    }`}
                  >
                    {integration.sync_as_lead ? 'Yes' : 'No'}
                  </span>
                </td>
              </tr>
            )}
            {organizationSettings?.sticky_chats_enabled !== undefined && (
              <tr>
                <td style={{ fontWeight: 'bold' }}>Smart Number Assignment</td>
                <td>
                  <span
                    className={`badge ${
                      organizationSettings.sticky_chats_enabled
                        ? 'bg-success'
                        : 'bg-secondary'
                    }`}
                  >
                    {organizationSettings.sticky_chats_enabled ? 'Yes' : 'No'}
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    )
  }

  const renderGHLUserMappings = () => {
    if (!isGHL) return null

    return (
      <div className="mt-4">
        <Accordion
          defaultActiveKey="0"
          onSelect={() => setGhlMappingsOpen(!ghlMappingsOpen)}
        >
          <Card>
            <Card.Header style={{ padding: 0 }}>
              <Accordion.Toggle
                as={Button}
                variant="link"
                eventKey="0"
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                  fontWeight: 'bold',
                  padding: '0.75rem 1.25rem',
                  width: '100%',
                  textAlign: 'left',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>GHL User Mappings</span>
                <span style={{ fontSize: '0.8em', marginLeft: '8px' }}>
                  {ghlMappingsOpen ? '▼' : '▶'}
                </span>
              </Accordion.Toggle>
            </Card.Header>
            <Accordion.Collapse eventKey="0">
              <Card.Body>
                {isLoadingGHLMappings ? (
                  <div className="text-center py-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">
                      Loading GHL user mappings...
                    </p>
                  </div>
                ) : ghlUserMappings ? (
                  <div>
                    <p className="text-muted mb-3">
                      Found {ghlUserMappings.ghl_users.length} GHL user(s) and{' '}
                      {Object.keys(ghlUserMappings.mappings).length} mapping(s)
                    </p>

                    {ghlUserMappings.ghl_users.length > 0 && (
                      <div className="mb-4">
                        <Table striped bordered hover responsive size="sm">
                          <thead>
                            <tr>
                              <th style={{ width: '50%' }}>GHL User</th>
                              <th style={{ width: '50%' }}>Mapped Linq User</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ghlUserMappings.ghl_users.map((ghlUser) => {
                              const mappedUserId =
                                ghlUserMappings.mappings[ghlUser.id]
                              const mappedUser =
                                ghlUserMappings.organization_users.find(
                                  (u) => u.id === mappedUserId
                                )

                              return (
                                <tr key={ghlUser.id}>
                                  <td>
                                    <div>
                                      <strong>{ghlUser.name}</strong>
                                      <br />
                                      <small className="text-muted">
                                        {ghlUser.email}
                                      </small>
                                      {ghlUser.phone && (
                                        <>
                                          <br />
                                          <small className="text-muted">
                                            📞 {ghlUser.phone}
                                          </small>
                                        </>
                                      )}
                                      {ghlUser.roles?.role && (
                                        <>
                                          <br />
                                          <span className="badge bg-info text-dark">
                                            {ghlUser.roles.role}
                                          </span>
                                          {ghlUser.roles.type && (
                                            <span className="badge bg-secondary ms-1">
                                              {ghlUser.roles.type}
                                            </span>
                                          )}
                                        </>
                                      )}
                                      {ghlUser.deleted && (
                                        <>
                                          <br />
                                          <span className="badge bg-danger">
                                            Deleted
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    {mappedUser ? (
                                      <div>
                                        <a
                                          href={`/user/${mappedUser.id}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          <strong>{mappedUser.name}</strong>
                                        </a>
                                        <br />
                                        <small className="text-muted">
                                          {mappedUser.email}
                                        </small>
                                        {mappedUser.phone_number && (
                                          <>
                                            <br />
                                            <small className="text-muted">
                                              📞 {mappedUser.phone_number}
                                            </small>
                                          </>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-muted fst-italic">
                                        No user mapping
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </Table>
                      </div>
                    )}

                    {Object.keys(ghlUserMappings.mappings).length === 0 && (
                      <p className="text-muted">No user mappings configured.</p>
                    )}
                  </div>
                ) : (
                  <p className="text-muted">
                    Failed to load GHL user mappings.
                  </p>
                )}
              </Card.Body>
            </Accordion.Collapse>
          </Card>
        </Accordion>
      </div>
    )
  }

  const renderHubSpotSpecificDetails = () => {
    if (!isHubSpot) return null

    return (
      <div className="mt-3">
        <h6>HubSpot Details</h6>
        <Table striped bordered hover responsive>
          <tbody>
            {integration.portal_id && (
              <tr>
                <td style={{ fontWeight: 'bold', width: '30%' }}>Portal ID</td>
                <td>{integration.portal_id}</td>
              </tr>
            )}
            {integration.state && (
              <tr>
                <td style={{ fontWeight: 'bold' }}>State</td>
                <td>
                  <code>{integration.state}</code>
                </td>
              </tr>
            )}
            {integration.version && (
              <tr>
                <td style={{ fontWeight: 'bold' }}>Version</td>
                <td>{integration.version}</td>
              </tr>
            )}
            {integration.sync_as_lead !== undefined && (
              <tr>
                <td style={{ fontWeight: 'bold' }}>Sync as Lead</td>
                <td>
                  <span
                    className={`badge ${
                      integration.sync_as_lead ? 'bg-success' : 'bg-secondary'
                    }`}
                  >
                    {integration.sync_as_lead ? 'Yes' : 'No'}
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    )
  }

  const renderGeneralIntegrationDetails = () => {
    const hasGeneralDetails =
      integration.webhook_url || integration.instance_url || integration.tenant

    if (!hasGeneralDetails) return null

    return (
      <div className="mt-3">
        <h6>Integration Configuration</h6>
        <Table striped bordered hover responsive>
          <tbody>
            {integration.webhook_url && (
              <tr>
                <td style={{ fontWeight: 'bold', width: '30%' }}>
                  Webhook URL
                </td>
                <td>
                  <code>{integration.webhook_url}</code>
                </td>
              </tr>
            )}
            {integration.instance_url && (
              <tr>
                <td style={{ fontWeight: 'bold' }}>Instance URL</td>
                <td>
                  <code>{integration.instance_url}</code>
                </td>
              </tr>
            )}
            {integration.tenant && (
              <tr>
                <td style={{ fontWeight: 'bold' }}>Tenant</td>
                <td>{integration.tenant}</td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    )
  }

  const renderSyncSettings = () => {
    if (!integration.settings) return null

    const { settings } = integration

    const syncSettingsToShow = [
      {
        key: 'two_way_sync',
        label: 'Two-Way Sync',
        value: settings.two_way_sync,
      },
      {
        key: 'sync_contacts',
        label: 'Sync Contacts',
        value: settings.sync_contacts,
      },
      { key: 'sync_calls', label: 'Sync Calls', value: settings.sync_calls },
      {
        key: 'sync_messages',
        label: 'Sync Messages',
        value: settings.sync_messages,
      },
      {
        key: 'sync_email_messages',
        label: 'Sync Email Messages',
        value: settings.sync_email_messages,
      },
      {
        key: 'sync_email_open_events',
        label: 'Sync Email Open Events',
        value: settings.sync_email_open_events,
      },
      {
        key: 'sync_contact_notes',
        label: 'Sync Contact Notes',
        value: settings.sync_contact_notes,
      },
      {
        key: 'create_new_contacts',
        label: 'Create New Contacts',
        value: settings.create_new_contacts,
      },
      {
        key: 'log_against_deals',
        label: 'Log Against Deals',
        value: settings.log_against_deals,
      },
    ]

    const callSyncSettings = [
      {
        key: 'sync_call_recordings',
        label: 'Sync Call Recordings',
        value: settings.sync_call_recordings,
      },
      {
        key: 'sync_outgoing_answered_calls',
        label: 'Sync Outgoing Answered Calls',
        value: settings.sync_outgoing_answered_calls,
      },
      {
        key: 'sync_outgoing_unanswered_calls',
        label: 'Sync Outgoing Unanswered Calls',
        value: settings.sync_outgoing_unanswered_calls,
      },
      {
        key: 'sync_incoming_answered_calls',
        label: 'Sync Incoming Answered Calls',
        value: settings.sync_incoming_answered_calls,
      },
      {
        key: 'sync_incoming_missed_calls',
        label: 'Sync Incoming Missed Calls',
        value: settings.sync_incoming_missed_calls,
      },
      {
        key: 'sync_outgoing_voicemails',
        label: 'Sync Outgoing Voicemails',
        value: settings.sync_outgoing_voicemails,
      },
    ]

    const smsSyncSettings = [
      {
        key: 'sync_incoming_sms',
        label: 'Sync Incoming SMS',
        value: settings.sync_incoming_sms,
      },
      {
        key: 'sync_outgoing_sms',
        label: 'Sync Outgoing SMS',
        value: settings.sync_outgoing_sms,
      },
    ]

    const renderSettingsSection = (
      title: string,
      settingsArray: Array<{
        key: string
        label: string
        value: boolean | undefined
      }>
    ) => {
      const visibleSettings = settingsArray.filter(
        (setting) => setting.value !== undefined
      )
      if (visibleSettings.length === 0) return null

      return (
        <div className="mb-3">
          <h6 className="mb-2">{title}</h6>
          <Table striped bordered hover responsive size="sm">
            <tbody>
              {visibleSettings.map((setting) => (
                <tr key={setting.key}>
                  <td style={{ fontWeight: 'bold', width: '70%' }}>
                    {setting.label}
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        setting.value ? 'bg-success' : 'bg-secondary'
                      }`}
                    >
                      {setting.value ? 'Yes' : 'No'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )
    }

    return (
      <div className="mt-4">
        <Accordion onSelect={() => setSyncSettingsOpen(!syncSettingsOpen)}>
          <Card>
            <Card.Header style={{ padding: 0 }}>
              <Accordion.Toggle
                as={Button}
                variant="link"
                eventKey="0"
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                  fontWeight: 'bold',
                  padding: '0.75rem 1.25rem',
                  width: '100%',
                  textAlign: 'left',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>Sync Settings</span>
                <span style={{ fontSize: '0.8em', marginLeft: '8px' }}>
                  {syncSettingsOpen ? '▼' : '▶'}
                </span>
              </Accordion.Toggle>
            </Card.Header>
            <Accordion.Collapse eventKey="0">
              <Card.Body>
                {settings.last_sync_at && (
                  <p className="text-muted mb-3">
                    Last sync: {moment(settings.last_sync_at).format('LLLL')} (
                    {moment(settings.last_sync_at).fromNow()})
                  </p>
                )}

                {renderSettingsSection('General Settings', syncSettingsToShow)}
                {renderSettingsSection('Call Sync Settings', callSyncSettings)}
                {renderSettingsSection('SMS Sync Settings', smsSyncSettings)}
              </Card.Body>
            </Accordion.Collapse>
          </Card>
        </Accordion>
      </div>
    )
  }

  return (
    <Modal size="xl" show={show} onHide={handleClose}>
      <Modal.Header>
        <Modal.Title>Integration Connection Details</Modal.Title>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          {!isAlreadyDisabled && (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={handleDisable}
              disabled={disableMutation.isLoading}
            >
              {disableMutation.isLoading
                ? 'Disabling...'
                : 'Disable Connection'}
            </Button>
          )}
          <Button variant="outline-secondary" size="sm" onClick={handleClose}>
            ×
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4">
          <h5>Integration: {integration.name}</h5>
          <p className="text-muted">
            View detailed information about this integration connection.
          </p>
        </div>

        <div className="row">
          <div className="col-md-6">
            <h6>Basic Information</h6>
            <Table striped bordered hover responsive size="sm">
              <tbody>
                <tr>
                  <td style={{ fontWeight: 'bold', width: '40%' }}>ID</td>
                  <td>{integration.id}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold' }}>Name</td>
                  <td>{integration.name}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold' }}>Type</td>
                  <td>{integration.type}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold' }}>Status</td>
                  <td>
                    <span
                      className={`badge ${
                        integration.status === 'ACTIVE' ||
                        integration.status === 'active'
                          ? 'bg-success'
                          : integration.status === 'INACTIVE' ||
                            integration.status === 'inactive'
                          ? 'bg-danger'
                          : 'bg-warning'
                      }`}
                    >
                      {integration.status}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold' }}>Enabled</td>
                  <td>
                    <span
                      className={`badge ${
                        integration.enabled ? 'bg-success' : 'bg-danger'
                      }`}
                    >
                      {integration.enabled ? 'Yes' : 'No'}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold' }}>Connected At</td>
                  <td>{moment(integration.connected_at).format('LLL')}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold' }}>Connected Since</td>
                  <td>{moment(integration.connected_at).fromNow()}</td>
                </tr>
              </tbody>
            </Table>
          </div>

          <div className="col-md-6">
            {renderGHLSpecificDetails()}
            {renderHubSpotSpecificDetails()}
            {renderGeneralIntegrationDetails()}
          </div>
        </div>

        {renderGHLUserMappings()}
        {renderSyncSettings()}
      </Modal.Body>
    </Modal>
  )
}

export default IntegrationConnectionDetailModal
